import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEventEdition } from '../contexts/EventEditionContext';
import { getCurrentEditionForSeries } from '../services/eventEditionService';

/**
 * Syncs the EventEditionContext from URL query parameters.
 *
 * Supported params:
 *   - `editionId`: specific edition (e.g. `kutc-2026`). Used as-is.
 *   - `series`: series slug (e.g. `kutc`, `mo`). Resolved to the current edition
 *     via `getCurrentEditionForSeries` (ongoing > next > most-recent-past).
 *
 * If `editionId` is present it wins over `series`.
 *
 * Makes URLs like `/participants?series=kutc` or `/register?series=kutc` work
 * without relying on the caller having set the event context beforehand.
 * This is the entry contract used by external sites (e.g. krultra.no event pages).
 *
 * Returns:
 *   - `resolving`: true while we're looking up the edition / setting context
 *   - `resolveError`: non-null if lookup failed (unknown series, no editions, etc.)
 */
export function useEditionFromUrl(): { resolving: boolean; resolveError: string | null } {
  const [searchParams] = useSearchParams();
  const { event, setEvent } = useEventEdition();
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const editionIdParam = searchParams.get('editionId');
  const seriesParam = searchParams.get('series');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setResolveError(null);

      // No URL params → nothing to do; let existing context (if any) remain.
      if (!editionIdParam && !seriesParam) return;

      // editionId takes precedence
      if (editionIdParam) {
        if (event?.id === editionIdParam) return; // already in sync
        try {
          setResolving(true);
          await setEvent(editionIdParam);
        } catch (err: any) {
          if (!cancelled) {
            console.warn('useEditionFromUrl: failed to load editionId', editionIdParam, err);
            setResolveError(err?.message || `Failed to load edition ${editionIdParam}`);
          }
        } finally {
          if (!cancelled) setResolving(false);
        }
        return;
      }

      // Otherwise resolve series → edition
      if (seriesParam) {
        try {
          setResolving(true);
          const edition = await getCurrentEditionForSeries(seriesParam);
          if (cancelled) return;
          if (!edition) {
            setResolveError(`No editions found for series "${seriesParam}"`);
            return;
          }
          if (event?.id === edition.id) return; // already in sync
          await setEvent(edition.id);
        } catch (err: any) {
          if (!cancelled) {
            console.warn('useEditionFromUrl: failed to resolve series', seriesParam, err);
            setResolveError(err?.message || `Failed to resolve series ${seriesParam}`);
          }
        } finally {
          if (!cancelled) setResolving(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // `setEvent` is memoized in the provider; `event?.id` included so we don't re-run
    // after the context reflects our own update.
  }, [editionIdParam, seriesParam, event?.id, setEvent]);

  return { resolving, resolveError };
}
